/**
 * Estrutura.jsx — Vista kanban horizontal da equipa.
 * Tab "Project Managers": colunas por PM, cada planner com chip(s) da(s) OM(s) associada(s).
 * Tab "Operational Managers": colunas por OM, cada planner com chip(s) do(s) PM(s) associado(s).
 */

import React, { useEffect, useState } from "react";
import { fetchEstrutura } from "../services/api";


export default function Estrutura() {
  const [vistaTab, setVistaTab]             = useState("pm");
  const [data, setData]                     = useState(null);
  const [loading, setLoading]               = useState(true);
  const [erro, setErro]                     = useState(null);
  const [headFiltro, setHeadFiltro]         = useState("");
  const [naoAtribAberto, setNaoAtribAberto] = useState(false);

  const carregar = (tab = vistaTab, hId = headFiltro) => {
    setLoading(true);
    const params = { tipo: tab, ...(hId ? { head_id: hId } : {}) };
    fetchEstrutura(params)
      .then(setData)
      .catch((e) => setErro(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { carregar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTab  = (tab) => { setVistaTab(tab); carregar(tab, headFiltro); };
  const handleHead = (val) => { setHeadFiltro(val); carregar(vistaTab, val); };

  if (erro) return <div style={styles.erro}>{erro}</div>;

  const heads                = data?.heads                ?? [];
  const project_managers     = data?.project_managers     ?? [];
  const operational_managers = data?.operational_managers ?? [];
  const nao_atribuidos       = data?.nao_atribuidos       ?? [];

  return (
    <div>
      {/* Topo: título + tabs + filtro de head */}
      <div style={styles.topoBar}>
        <div style={styles.topoEsquerda}>
          <h1 style={styles.titulo}>Visão de Equipa</h1>
          <div style={styles.tabBar}>
            <button
              style={{ ...styles.tab, ...(vistaTab === "pm" ? styles.tabAtivo : {}) }}
              onClick={() => handleTab("pm")}
            >
              Project Managers
            </button>
            <button
              style={{ ...styles.tab, ...(vistaTab === "om" ? styles.tabAtivo : {}) }}
              onClick={() => handleTab("om")}
            >
              Operational Managers
            </button>
          </div>
        </div>
        {heads.length > 0 && (
          <select
            style={styles.selectHead}
            value={headFiltro}
            onChange={(e) => handleHead(e.target.value)}
          >
            <option value="">Todas as Heads</option>
            {heads.map((h) => (
              <option key={h.id} value={h.id}>{h.nome}</option>
            ))}
          </select>
        )}
      </div>

      {loading && <p style={styles.loading}>A carregar…</p>}

      {/* ── Vista Project Managers ─────────────────────────────────────────── */}
      {vistaTab === "pm" && (
        <div className="estrutura-board" style={styles.board}>
          {project_managers.map((pm) => (
            <div key={pm.pm_id} style={styles.coluna}>
              <div style={styles.colunaHeader}>
                <span style={styles.membroNome}>{pm.pm_nome}</span>
                <div style={styles.membroStats}>
                  <span style={styles.statChip}>{pm.total_planners} planners</span>
                  <span style={styles.statChip}>{pm.total_ativas} ativas</span>
                  {pm.total_risco > 0
                    ? <span style={{ ...styles.statChip, ...styles.statRisco }}>⚠️ {pm.total_risco} próximo</span>
                    : <span style={{ ...styles.statChip, ...styles.statOk }}>✅ ok</span>
                  }
                </div>
              </div>

              <div style={styles.colunaBody}>
                {pm.planners.length === 0 ? (
                  <p style={styles.semPlanners}>Sem planners atribuídos.</p>
                ) : (
                  pm.planners.map((p) => (
                    <PlannerRow
                      key={p.plan_id}
                      planner={p}
                      chips={(p.operacionais ?? []).map((om) => ({
                        key: om.id,
                        label: om.nome,
                        apoio: om.tipo === "apoio",
                        recente: om.estado === "apoio_recente",
                        title: om.estado === "apoio_recente"
                          ? "Sem tarefas ativas — atividade nos últimos 30 dias"
                          : undefined,
                      }))}
                    />
                  ))
                )}
              </div>
            </div>
          ))}

          {/* Planners não atribuídos — colapsável */}
          {nao_atribuidos.length > 0 && (
            <div style={{ ...styles.coluna, ...styles.colunaNaoAtribuida }}>
              <button
                style={{ ...styles.colunaHeader, ...styles.colunaHeaderNaoAtribuida, cursor: "pointer", width: "100%", border: "none" }}
                onClick={() => setNaoAtribAberto((v) => !v)}
              >
                <span style={styles.membroNome}>Não atribuídos</span>
                <div style={styles.membroStats}>
                  <span style={{ ...styles.statChip, ...styles.statNaoAtrib }}>
                    {nao_atribuidos.length} planners
                  </span>
                  <span style={{ fontSize: 11, color: "#92400e", marginLeft: 2 }}>
                    {naoAtribAberto ? "▲" : "▼"}
                  </span>
                </div>
              </button>
              {naoAtribAberto && (
                <div style={styles.colunaBody}>
                  {nao_atribuidos.map((p) => (
                    <div
                      key={p.plan_id}
                      style={{
                        ...styles.plannerRow,
                        ...styles.plannerRowNaoAtrib,
                        ...(p.inativo ? styles.plannerRowInativo : {}),
                      }}
                      title={p.inativo ? "Sem tarefas ativas" : undefined}
                    >
                      <span style={p.inativo ? { ...styles.plannerNome, ...styles.plannerNomeInativo } : styles.plannerNome} title={p.plan_title}>
                        {p.plan_title}
                      </span>
                      <span style={styles.plannerAtivas}>{p.total_ativas > 0 ? p.total_ativas : "—"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Vista Operational Managers ────────────────────────────────────── */}
      {vistaTab === "om" && (
        <div className="estrutura-board" style={styles.board}>
          {operational_managers.length === 0 && !loading && (
            <p style={styles.semDados}>Nenhum Operational Manager configurado.</p>
          )}
          {operational_managers.filter((om) => om.om_tipo !== "apoio").map((om) => (
            <OMColuna key={om.om_id} om={om} />
          ))}
          {operational_managers.some((om) => om.om_tipo === "apoio") && (
            <div style={styles.divisorApoio}>Apoio</div>
          )}
          {operational_managers.filter((om) => om.om_tipo === "apoio").map((om) => (
            <OMColuna key={om.om_id} om={om} />
          ))}
        </div>
      )}
    </div>
  );
}

function OMColuna({ om }) {
  return (
    <div style={styles.coluna}>
      <div style={styles.colunaHeader}>
        <span style={styles.membroNome}>
          {om.om_nome}
          {om.om_tipo === "apoio" && <span style={styles.chipApoioHeader}>Apoio</span>}
        </span>
        <div style={styles.membroStats}>
          <span style={styles.statChip}>{om.total_planners} planners</span>
          <span style={styles.statChip}>{om.total_ativas} ativas</span>
          {om.total_risco > 0
            ? <span style={{ ...styles.statChip, ...styles.statRisco }}>⚠️ {om.total_risco} próximo</span>
            : <span style={{ ...styles.statChip, ...styles.statOk }}>✅ ok</span>
          }
        </div>
      </div>

      <div style={styles.colunaBody}>
        {om.planners.length === 0 ? (
          <p style={styles.semPlanners}>Sem planners atribuídos.</p>
        ) : (
          om.planners.map((p) => (
            <PlannerRow
              key={p.plan_id}
              planner={p}
              chips={(p.pm_nomes ?? []).map((nome, i) => ({ key: i, label: nome }))}
            />
          ))
        )}
      </div>
    </div>
  );
}

function PlannerRow({ planner: p, chips = [] }) {
  return (
    <div
      style={p.inativo ? { ...styles.plannerRow, ...styles.plannerRowInativo } : styles.plannerRow}
      title={p.inativo ? "Sem tarefas ativas" : undefined}
    >
      <span style={p.inativo ? { ...styles.plannerNome, ...styles.plannerNomeInativo } : styles.plannerNome} title={p.plan_title}>
        {p.plan_title}
      </span>
      {chips.length > 0 && (
        <div style={styles.plannerChips}>
          {chips.map((c) => (
            <span
              key={c.key}
              title={c.title}
              style={{
                ...styles.plannerChip,
                ...(c.apoio ? styles.plannerChipApoio : {}),
                ...(c.recente ? styles.plannerChipRecente : {}),
              }}
            >
              {c.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

const COL_WIDTH = 300;
const MAX_COLS  = 4;

const styles = {
  topoBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 20,
    gap: 16,
  },
  topoEsquerda: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  titulo: { fontSize: 24, fontWeight: 700, margin: 0, color: "#1e293b" },

  tabBar: {
    display: "flex",
    gap: 4,
    background: "#f1f5f9",
    borderRadius: 8,
    padding: 4,
  },
  tab: {
    padding: "6px 14px",
    fontSize: 13,
    fontWeight: 500,
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    background: "transparent",
    color: "#64748b",
    transition: "background 0.12s, color 0.12s",
  },
  tabAtivo: {
    background: "#ffffff",
    color: "#1e293b",
    fontWeight: 600,
    boxShadow: "0 1px 3px rgba(0,0,0,.08)",
  },

  selectHead: {
    padding: "8px 14px",
    border: "1px solid #e2e8f0",
    borderRadius: 6,
    fontSize: 13,
    backgroundColor: "#fff",
    color: "#374151",
    cursor: "pointer",
    minWidth: 200,
    marginTop: 4,
  },

  board: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    alignItems: "flex-start",
  },

  coluna: {
    width: COL_WIDTH,
    minWidth: COL_WIDTH,
    maxWidth: `calc((100% - ${(MAX_COLS - 1) * 12}px) / ${MAX_COLS})`,
    flex: `0 0 ${COL_WIDTH}px`,
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    overflow: "hidden",
  },

  colunaNaoAtribuida: {
    backgroundColor: "#fffbeb",
    border: "1px solid #fde68a",
  },

  colunaHeader: {
    padding: "14px 14px 12px",
    backgroundColor: "#fff",
    borderBottom: "1px solid #e2e8f0",
  },

  colunaHeaderNaoAtribuida: {
    backgroundColor: "#fef3c7",
    borderBottom: "1px solid #fde68a",
    textAlign: "left",
  },

  membroNome: {
    display: "block",
    fontSize: 13,
    fontWeight: 700,
    color: "#1e293b",
    marginBottom: 8,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  membroStats: {
    display: "flex",
    gap: 4,
    flexWrap: "wrap",
  },

  statChip: {
    fontSize: 11,
    fontWeight: 500,
    backgroundColor: "#f1f5f9",
    color: "#64748b",
    borderRadius: 10,
    padding: "2px 8px",
  },
  statRisco: {
    backgroundColor: "#fee2e2",
    color: "#dc2626",
    fontWeight: 700,
  },
  statOk: {
    backgroundColor: "#f0fdf4",
    color: "#16a34a",
  },
  statNaoAtrib: {
    backgroundColor: "#fde68a",
    color: "#92400e",
  },

  colunaBody: {
    padding: "8px 8px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    maxHeight: "calc(100vh - 280px)",
    overflowY: "auto",
  },

  plannerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    backgroundColor: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 6,
    padding: "7px 10px",
    gap: 8,
  },
  plannerRowNaoAtrib: {
    backgroundColor: "#fffbeb",
    borderColor: "#fde68a",
  },
  plannerRowInativo: {
    backgroundColor: "#f8fafc",
    borderColor: "#e2e8f0",
  },
  plannerNomeInativo: {
    color: "#94a3b8",
  },

  plannerChips: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    gap: 3,
    flexShrink: 0,
  },
  plannerChip: {
    fontSize: 10,
    color: "#6366f1",
    backgroundColor: "#eef2ff",
    borderRadius: 8,
    padding: "1px 6px",
    fontWeight: 500,
  },
  plannerChipRecente: {
    color: "#94a3b8",
    backgroundColor: "#f1f5f9",
    fontStyle: "italic",
  },
  plannerChipApoio: {
    color: "#9333ea",
    backgroundColor: "#f3e8ff",
  },

  divisorApoio: {
    width: "100%",
    fontSize: 11,
    fontWeight: 700,
    color: "#9333ea",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    padding: "6px 2px",
    borderTop: "1px solid #e9d5ff",
    marginTop: 4,
  },
  chipApoioHeader: {
    marginLeft: 6,
    padding: "2px 8px",
    borderRadius: 999,
    background: "#f3e8ff",
    color: "#9333ea",
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },

  plannerNome: {
    fontSize: 12,
    color: "#374151",
    fontWeight: 500,
    flex: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  plannerAtivas: {
    fontSize: 11,
    fontWeight: 700,
    color: "#6366f1",
    minWidth: 18,
    textAlign: "right",
    flexShrink: 0,
  },

  semPlanners: {
    fontSize: 12,
    color: "#94a3b8",
    padding: "8px 4px",
    margin: 0,
  },
  semDados: {
    fontSize: 14,
    color: "#94a3b8",
  },

  loading: { color: "#64748b", marginTop: 40 },
  erro: {
    background: "#fef2f2",
    border: "1px solid #fca5a5",
    borderRadius: 6,
    padding: 16,
    color: "#dc2626",
  },
};
